import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import { DataGrid } from "@mui/x-data-grid";
import SideBar from "../components/SideBar";
import axios from "axios";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import CancelShipmentModal from "./CancelShipmentModal";
import EvalIcon from "../assets/images/assessment.png";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";
import EvaluationModal from "./EvaluationModal";

const getColumns = (handleOpenModal) => [
  { field: "ContactUsID", headerName: "ID", width: 150 },
  { field: "TransDate", headerName: "Date", width: 200 },
  { field: "TypeEnglishName", headerName: "Type Name", width: 150 },
  { field: "Subject", headerName: "Notes", width: 200 },
  { field: "Details", headerName: "Details", width: 200 },
  { field: "AgentName", headerName: "Agent Name", width: 150 },
  {
    field: "actions",
    headerName: "Actions",
    width: 150,
    sortable: false,
    renderCell: (params) => (
      <Box display="flex" gap={1}>
        <IconButton color="secondary">
          <CancelIcon sx={{ color: "red" }} />
        </IconButton>
        <img
          src={EvalIcon}
          alt="eValuation"
          onClick={() => handleOpenModal()}
          width={30}
        />
      </Box>
    ),
  },
];

const validationSchema = Yup.object({
  fromDate: Yup.date().required("From Date is required"),
  toDate: Yup.date()
    .required("To Date is required")
    .min(Yup.ref("fromDate"), "To Date must be later than From Date"),
  selectedUser: Yup.number().required("User selection is required"),
});

export default function AllCalls() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  // الدالة لتصدير البيانات إلى Excel
  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Shipments");
    XLSX.writeFile(workbook, "shipments.xlsx");
  };

  const { data: usersData = [], isLoading: isLoadingUsers } = useQuery(
    "users",
    async () => {
      const userData = JSON.parse(localStorage.getItem("userData"));
      const response = await axios.get(
        "https://tarabotcs.com-eg.net/api/Users/V1/GetUsers",
        {
          headers: {
            CompanyID: userData.Data.CompanyID,
            AccessToken: userData.Data.AccessToken,
          },
        },
      );
      return response.data.Data;
    },
    {
      onError: (error) => {
        // التحقق إذا كانت حالة الرد 401 Unauthorized
        if (error.response && error.response.status === 401) {
          console.log("Session expired, logging out...");
          localStorage.removeItem("userData"); // مسح بيانات المستخدم
          navigate("/"); // إعادة التوجيه إلى الصفحة الرئيسية
        } else {
          console.error("خطأ في جلب المستخدمين:", error);
        }
      },
    },
  );

  const fetchShipments = async (values) => {
    setLoading(true);
    setError(null);
    setData([]);

    try {
      const userData = JSON.parse(localStorage.getItem("userData"));
      if (!userData) {
        navigate("/");
        return;
      }

      let allCalls = [];
      let pageNumber = 1;
      const pageSize = 100;
      let hasMore = true;
      const seenIds = new Set();

      while (hasMore) {
        const response = await axios.post(
          "https://tarabotcs.com-eg.net/api/Admins/V1/GetCallsList",
          {
            pageParam: {
              PageSize: pageSize,
              PageNumber: pageNumber,
            },
            FromDate: new Date(values.fromDate).toISOString(),
            ToDate: new Date(values.toDate).toISOString(),
            AgentID: values.selectedUser || 0,
          },
          {
            headers: {
              CompanyID: userData.Data.CompanyID,
              AccessToken: userData.Data.AccessToken,
            },
          },
        );

        const fetchedData = response.data?.Data;

        if (Array.isArray(fetchedData) && fetchedData.length > 0) {
          // فحص أمان لمنع التكرار إذا كان الخادم لا يدعم زيادة رقم الصفحة
          const firstItem = fetchedData[0];
          const firstId = firstItem.ContactUsID || firstItem.id;
          if (firstId && seenIds.has(firstId)) {
            break;
          }

          fetchedData.forEach((item) => {
            const id = item.ContactUsID || item.id;
            if (id) seenIds.add(id);
          });

          allCalls = [...allCalls, ...fetchedData];
          setData([...allCalls]);

          // إذا رجع أقل من 100 عنصر، فهذه آخر صفحة
          if (fetchedData.length < pageSize) {
            hasMore = false;
          } else {
            pageNumber += 1;
          }
        } else {
          hasMore = false;
        }
      }
    } catch (err) {
      if (err.response && err.response.status === 401) {
        console.log("Session expired, logging out...");
        localStorage.removeItem("userData");
        navigate("/");
      } else {
        setError("Error loading shipments");
      }
    } finally {
      setLoading(false);
    }
  };

  // استخدام useForm مع yupResolver
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  return (
    <Box sx={{ display: "flex", height: "calc(100vh - 100px)" }}>
      <SideBar />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          height: "calc(100vh - 100px)",
          padding: 2,
          marginTop: "70px",
        }}
      >
        <form onSubmit={handleSubmit(fetchShipments)}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              marginBottom: 4,
            }}
          >
            {/* حقل إدخال تاريخ البداية */}
            <Controller
              name="fromDate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("fromDate")}
                  variant="outlined"
                  type="date"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  error={!!errors.fromDate}
                  helperText={errors.fromDate ? errors.fromDate.message : ""}
                />
              )}
            />
            {/* حقل إدخال تاريخ النهاية */}
            <Controller
              name="toDate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("toDate")}
                  variant="outlined"
                  type="date"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  error={!!errors.toDate}
                  helperText={errors.toDate ? errors.toDate.message : ""}
                />
              )}
            />
            <FormControl variant="outlined" sx={{ minWidth: 150 }}>
              <InputLabel id="user-select-label">Select User</InputLabel>
              <Controller
                name="selectedUser"
                control={control}
                defaultValue={0} // القيمة الافتراضية
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="user-select-label"
                    label={t("selectUser")}
                    onChange={(e) => field.onChange(e.target.value)}
                    error={!!errors.selectedUser}
                  >
                    <MenuItem value={0}>All</MenuItem> {/* إضافة خيار All */}
                    {isLoadingUsers ? (
                      <MenuItem disabled>Loading...</MenuItem>
                    ) : (
                      usersData.map((user) => (
                        <MenuItem key={user.UserID} value={user.UserID}>
                          {user.Username}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                )}
              />
              {errors.selectedUser && (
                <span style={{ color: "red" }}>
                  {errors.selectedUser.message}
                </span>
              )}
            </FormControl>
            {/* زر البحث */}
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                backgroundColor: "#2563eb",
                color: "white",
                width: "150px",
                borderRadius: "8px",
                fontWeight: 700,
                "&:hover": { backgroundColor: "#1d4ed8" },
              }}
            >
              {loading ? (
                <CircularProgress size={22} sx={{ color: "white" }} />
              ) : (
                t("search")
              )}
            </Button>
          </Box>
        </form>

        {/* زر تصدير إلى Excel و PDF */}
        <Box sx={{ display: "flex", gap: 2, marginBottom: 2 }}>
          <Button
            variant="contained"
            onClick={exportToExcel}
            disabled={loading || data.length === 0}
            sx={{
              backgroundColor: "#10b981",
              color: "white",
              borderRadius: "8px",
              fontWeight: 700,
              "&:hover": { backgroundColor: "#059669" },
            }}
          >
            {t("exportExcel")}
          </Button>
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          {error ? (
            <div style={{ color: "red", padding: "16px", textAlign: "center" }}>
              {error}
            </div>
          ) : loading && data.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "300px",
                gap: 2,
              }}
            >
              <CircularProgress size={40} sx={{ color: "#2563eb" }} />
              <span style={{ color: "#64748b", fontWeight: 600 }}>
                جاري جلب البيانات...
              </span>
            </Box>
          ) : data && data.length > 0 ? (
            <DataGrid
              rows={data}
              columns={getColumns(handleOpenModal)}
              getRowId={(row) => row.ContactUsID}
              loading={loading}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 100,
                  },
                },
              }}
              pageSizeOptions={[50, 100, 200]}
              checkboxSelection
              disableRowSelectionOnClick
              sx={{
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#1e293b",
                  color: "#ffffff",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold",
                },
                "& .MuiDataGrid-row:nth-of-type(even)": {
                  backgroundColor: "#f8fafc",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#eff6ff",
                },
                height: "100%",
              }}
            />
          ) : (
            <div>{t("noShipmentFound")}</div>
          )}
        </Box>
      </Box>
      <EvaluationModal open={openModal} handleClose={handleCloseModal} />
    </Box>
  );
}
